<template>
  <!--
    Product cards render their OWN responsive <picture> via this atom imported
    DIRECTLY (ResponsiveImage = src/core/.../atoms/image/image.vue). We do NOT use
    the global <vf-a-image>, because the build maps that tag to
    checkout-app/lib/.../image.vue (the old background-image atom that ignores
    srcset). Importing the real atom here keeps the change scoped to product
    cards only — cart/checkout (which still use vf-a-image) are untouched.
  -->
  <ResponsiveImage
    :src="mainImage"
    :srcset="avifSrcset"
    :webp-src="webpSrc"
    :sizes="sizesAttr"
    :alt="productImageAlt"
    :title="productImageAlt"
    :width-amp="width ? width : $vuefront.images.productThumb.width"
    :height-amp="height ? height : $vuefront.images.productThumb.height"
    :width="width ? width : null"
    :class="{ 'card-img': card }"
    :fluid="!width && !height"
    :eager="priority"
    class="vf-m-new-product-thumb-image"
    @load="emit('image:settled')"
    @error="handleImageError"
  />
</template>
<script setup lang="ts">
import { inject, PropType, computed, ref } from "vue";
import {
  productAvifSrcset,
  productWebpSrc,
  hasRenditions,
  renditionUrl,
} from "~/utils/cdn";
import { buildProductCardImageAlt } from "~/core/utils/productImageAlt";
// Import the real responsive <picture> atom directly (NOT the global vf-a-image,
// which resolves to the old checkout-app background-image atom). Scoped to cards.
// The structural .vf-a-image* classes are styled by the global (checkout-app)
// image.scss already loaded site-wide; the card image's fit/sizing is pinned
// deterministically in this component's <style> below.
import ResponsiveImage from "~/core/components/atoms/image/image.vue";

const vuefront$ = inject<any>("$vuefront");
const emit = defineEmits(["image:settled"]);
const props = defineProps({
  card: {
    type: Boolean,
    default() {
      return false;
    },
  },
  product: {
    type: Object,
    default() {
      return null;
    },
  },
  width: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return null;
    },
  },
  height: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return null;
    },
  },
  layout: {
    type: String,
    default() {
      return null;
    },
  },
  priority: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const imageFailed = ref(false);
// Reuse one deterministic SEO alt across every rendition of this card image.
const productImageAlt = computed(() => buildProductCardImageAlt(props.product));

// Cards render small (<=300px CSS, <=~600px at retina), so the 600 square AVIF
// is the right size at every breakpoint - mobile and desktop. Use it directly as
// the src when the product has AVIF renditions, so the card stops over-fetching
// the full-resolution base image even where vf-a-image renders a background-image
// rather than an <img>. Products with no AVIF master keep their raster image.
// On error, fall back to the product alt image or the global placeholder.
const mainImage = computed(() => {
  const src = props.product?.image;
  if (!src) return vuefront$.images.placeholder.image;
  if (imageFailed.value)
    return props.product.imageAlt || vuefront$.images.placeholder.image;
  return hasRenditions(src)
    ? renditionUrl(src, 600, { ratio: 1, format: "avif" }) || src
    : src;
});

// Responsive AVIF (+ base WebP) derived from the API image URL. Both are empty
// strings when the product has no renditions (a .png/.jpg master) or after an
// error, so vf-a-image renders a plain img with no broken sources / 404s.
const avifSrcset = computed(() =>
  imageFailed.value ? "" : productAvifSrcset(props.product?.image)
);
const webpSrc = computed(() =>
  imageFailed.value ? "" : productWebpSrc(props.product?.image)
);

// Catalog/related/home cards. The real card image box is ~178px (mobile) →
// ~300px (desktop). Declaring the true desktop box (300px, not 25vw≈360) keeps
// DPR2 desktop on the 600 tier (300×2=600) instead of jumping to 1200 — ~3.5×
// lighter per card. Mobile/tablet stay at 600 across DPR1–3. Only DPR3 desktop
// (rare) is marginally soft.
const sizesAttr = "(max-width: 600px) 50vw, (max-width: 1024px) 33vw, 300px";

function handleImageError() {
  imageFailed.value = true;
  emit("image:settled");
}
</script>

<style>
/*
  The card image is now a real <img> inside the imported <picture> atom (it used
  to be a CSS background-image div). Pin it to fill the card box and show the
  WHOLE product un-cropped — object-fit: contain, matching the previous
  background-size: contain look (NOT cover, which zoomed/cropped it). This
  selector (.vf-m-new-product-thumb-image .vf-a-image__img) is more specific than
  the atom's .vf-a-image__img / --cover rules, so it deterministically wins
  regardless of which image.scss loads last. Scoped to product cards only.
*/
.vf-m-new-product-thumb-image .vf-a-image__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: transparent;
}
</style>
